import { 
  startOfDay, 
  endOfDay, 
  addDays, 
  differenceInDays,
  format,
  isWeekend,
  isToday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  eachWeekOfInterval,
  eachMonthOfInterval
} from 'date-fns';
import { hu } from 'date-fns/locale';
import { 
  GanttTask, 
  TimelineColumn, 
  GanttViewSettings,
  GANTT_DAY_WIDTH,
  GANTT_WEEK_WIDTH,
  GANTT_MONTH_WIDTH
} from '../types/gantt';

// ============================================
// DATE CALCULATIONS
// ============================================

/**
 * Calculate the date range for the Gantt chart
 */
export const calculateDateRange = (
  tasks: GanttTask[],
  settings: GanttViewSettings
): { start: Date; end: Date } => {
  if (settings.startDate && settings.endDate) {
    return {
      start: startOfDay(settings.startDate),
      end: endOfDay(settings.endDate)
    };
  }

  if (tasks.length === 0) {
    const today = new Date();
    return {
      start: startOfDay(addDays(today, -30)),
      end: endOfDay(addDays(today, 60))
    };
  }

  // Find earliest start and latest end
  const dates = tasks.flatMap(task => [task.start, task.end]);
  const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

  // Add padding
  return {
    start: startOfDay(addDays(minDate, -7)),
    end: endOfDay(addDays(maxDate, 7))
  };
};

/**
 * Generate timeline columns based on time scale
 */
export const generateTimelineColumns = (
  startDate: Date,
  endDate: Date,
  timeScale: GanttViewSettings['timeScale']
): TimelineColumn[] => {
  const columns: TimelineColumn[] = [];

  switch (timeScale) {
    case 'day':
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      days.forEach(date => {
        columns.push({
          date,
          label: format(date, 'MMM d', { locale: hu }),
          isWeekend: isWeekend(date),
          isToday: isToday(date),
          type: 'day'
        });
      });
      break;

    case 'week':
      const weeks = eachWeekOfInterval(
        { start: startDate, end: endDate },
        { weekStartsOn: 1 } // Monday
      );
      weeks.forEach(date => {
        const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
        columns.push({
          date,
          label: `${format(date, 'MMM d', { locale: hu })} - ${format(weekEnd, 'd', { locale: hu })}`,
          isWeekend: false,
          isToday: isToday(date),
          type: 'week'
        });
      });
      break;

    case 'month':
      const months = eachMonthOfInterval({ start: startDate, end: endDate });
      months.forEach(date => {
        columns.push({
          date,
          label: format(date, 'MMMM yyyy', { locale: hu }),
          isWeekend: false,
          isToday: isToday(date),
          type: 'month'
        });
      });
      break;

    case 'quarter':
      // Simplified quarter view - group by 3 months
      const quarterMonths = eachMonthOfInterval({ start: startDate, end: endDate });
      for (let i = 0; i < quarterMonths.length; i += 3) {
        const date = quarterMonths[i];
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        columns.push({
          date,
          label: `Q${quarter} ${date.getFullYear()}`,
          isWeekend: false,
          isToday: false,
          type: 'month'
        });
      }
      break;
  }

  return columns;
};

/**
 * Get column width based on time scale
 */
export const getColumnWidth = (timeScale: GanttViewSettings['timeScale']): number => {
  switch (timeScale) {
    case 'day':
      return GANTT_DAY_WIDTH;
    case 'week':
      return GANTT_WEEK_WIDTH;
    case 'month':
    case 'quarter':
      return GANTT_MONTH_WIDTH;
    default:
      return GANTT_DAY_WIDTH;
  }
};

// ============================================
// TASK BAR CALCULATIONS
// ============================================

/**
 * Calculate task bar position and width
 */
export const calculateTaskBarPosition = (
  task: GanttTask,
  startDate: Date,
  timeScale: GanttViewSettings['timeScale']
): { x: number; width: number } => {
  const columnWidth = getColumnWidth(timeScale);
  
  // Calculate days from start
  const daysFromStart = differenceInDays(startOfDay(task.start), startOfDay(startDate));
  const taskDuration = differenceInDays(startOfDay(task.end), startOfDay(task.start)) + 1;

  let x = 0;
  let width = 0;

  switch (timeScale) {
    case 'day':
      x = daysFromStart * columnWidth;
      width = taskDuration * columnWidth;
      break;

    case 'week':
      x = Math.floor(daysFromStart / 7) * columnWidth;
      width = Math.ceil(taskDuration / 7) * columnWidth;
      break;

    case 'month':
      // Approximate month calculation
      const monthsFromStart = Math.floor(daysFromStart / 30);
      const taskMonths = Math.ceil(taskDuration / 30);
      x = monthsFromStart * columnWidth;
      width = taskMonths * columnWidth;
      break;

    case 'quarter':
      // Approximate quarter calculation
      const quartersFromStart = Math.floor(daysFromStart / 90);
      const taskQuarters = Math.ceil(taskDuration / 90);
      x = quartersFromStart * columnWidth;
      width = taskQuarters * columnWidth;
      break;
  }

  // Minimum width
  width = Math.max(width, columnWidth * 0.5);

  return { x, width };
};

/**
 * Check if task is overdue
 */
export const isTaskOverdue = (task: GanttTask): boolean => {
  if (task.status === 'completed' || task.status === 'cancelled') {
    return false;
  }
  return task.end < new Date();
};

/**
 * Calculate task duration in days
 */
export const calculateTaskDuration = (task: GanttTask): number => {
  return differenceInDays(startOfDay(task.end), startOfDay(task.start)) + 1;
};

// ============================================
// GROUPING AND SORTING
// ============================================

/**
 * Group tasks by specified criteria
 */
export const groupTasks = (
  tasks: GanttTask[],
  groupBy: GanttViewSettings['groupBy']
): Map<string, GanttTask[]> => {
  const groups = new Map<string, GanttTask[]>();

  if (groupBy === 'none') {
    groups.set('all', tasks);
    return groups;
  }

  tasks.forEach(task => {
    let key = '';
    
    switch (groupBy) {
      case 'status':
        key = task.status;
        break;
      case 'priority':
        key = task.priority;
        break;
      case 'assignee':
        key = task.assignedToName || 'Nincs felelős';
        break;
      default:
        key = 'all';
    }

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(task);
  });

  return groups;
};

/**
 * Sort tasks by start date
 */
export const sortTasksByDate = (tasks: GanttTask[]): GanttTask[] => {
  return [...tasks].sort((a, b) => a.start.getTime() - b.start.getTime());
};

// ============================================
// DEPENDENCY CALCULATIONS
// ============================================

/**
 * Calculate dependency line path
 */
export const calculateDependencyPath = (
  fromTask: GanttTask,
  toTask: GanttTask,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
): string => {
  // Simple L-shaped path
  const midY = (fromY + toY) / 2;
  
  return `M ${fromX} ${fromY} 
          L ${fromX + 20} ${fromY} 
          L ${fromX + 20} ${midY} 
          L ${toX - 20} ${midY} 
          L ${toX - 20} ${toY} 
          L ${toX} ${toY}`;
};

// ============================================
// FORMATTING
// ============================================

/**
 * Format date range for display
 */
export const formatDateRange = (start: Date, end: Date): string => {
  return `${format(start, 'MMM d, yyyy', { locale: hu })} - ${format(end, 'MMM d, yyyy', { locale: hu })}`;
};

/**
 * Format task duration for display
 */
export const formatDuration = (days: number): string => {
  if (days === 1) return '1 nap';
  if (days < 7) return `${days} nap`;
  
  const weeks = Math.floor(days / 7);
  const remainingDays = days % 7;
  
  if (remainingDays === 0) {
    return weeks === 1 ? '1 hét' : `${weeks} hét`;
  }
  
  return `${weeks} hét ${remainingDays} nap`;
};

/**
 * Get status label in Hungarian
 */
export const getStatusLabel = (status: GanttTask['status']): string => {
  const labels = {
    todo: 'Tennivaló',
    in_progress: 'Folyamatban',
    completed: 'Kész',
    cancelled: 'Megszakítva'
  };
  return labels[status];
};

/**
 * Get priority label in Hungarian
 */
export const getPriorityLabel = (priority: GanttTask['priority']): string => {
  const labels = {
    low: 'Alacsony',
    medium: 'Közepes',
    high: 'Magas',
    urgent: 'Sürgős'
  };
  return labels[priority];
};
